import { Location } from '@angular/common';
import { Directive } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { marker } from '@common/translate-marker';
import { ViewMode } from '@common/models/view-mode';
import { requiredValidator } from '@common/validators/required-validator';
import { TranslateService } from '@ngx-translate/core';
import { SelectEvent } from '@progress/kendo-angular-layout';
import { FormSafeNavigationService } from 'app/singleton-services/form-safe-navigation.service';
import { ToastService } from '@services/toast.service';
import { Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { ModalService } from '../services/modal.service';
import { FormBase } from './form-base';

@Directive()
export class BaseRestEditController extends FormBase {
    mode: ViewMode;
    modeType = ViewMode;
    model: any;
    activeTab: string;
    isBusy: boolean;
    tabBusy: boolean;

    editMode: boolean;
    createMode: boolean;
    viewMode: boolean;
    cloneMode: boolean;
    printMode: boolean;

    /** Get id of current entity from activatedRoute "id" parameter */
    entityId: number;

    destroyed$ = new Subject<void>();

    constructor(
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        protected toastyService: ToastService,
        protected location: Location,
        dialogService: ModalService,
        protected translateService: TranslateService,
        formSafeNavigation: FormSafeNavigationService
    ) {
        super(dialogService, translateService, formSafeNavigation);

        this.setMode(this.activatedRoute.snapshot);

        this.entityId = +this.activatedRoute.snapshot.params.id;

        this.initialSelectedTabIndex =
            this.router.routerState.snapshot.root.queryParams.tab === undefined
                ? 0
                : parseInt(this.router.routerState.snapshot.root.queryParams.tab, null);

        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {
                tab: this.initialSelectedTabIndex
            },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    setMode(routeSnapshot: ActivatedRouteSnapshot) {
        this.mode = routeSnapshot.data.mode;
        this.editMode = routeSnapshot.data.mode === ViewMode.edit;
        this.createMode = routeSnapshot.data.mode === ViewMode.create;
        this.viewMode = routeSnapshot.data.mode === ViewMode.view;
        this.cloneMode = routeSnapshot.data.mode === ViewMode.clone;
        this.printMode = routeSnapshot.data.mode === ViewMode.print;
    }

    get disableSave() {
        return this.isBusy || !(this.form && this.form.touched && this.form.valid);
    }

    protected goBack(explicitRoute?: string) {
        if (explicitRoute) {
            this.router.navigate([`/${explicitRoute}`]);
            return;
        }
        // Rely on browser history. The tab query param is set with replaceUrl,
        // so the edit page occupies a single history entry and location.back()
        // returns to the originating list. Do not use document.referrer here:
        // in a SPA it reflects the initial full-page load, not the in-app
        // previous route, so it sends Back to the wrong list.
        this.location.back();
    }

    public localizeToastySuccessMessage(message: string) {
        this.toastyService.success(this.translateService.instant(marker(message)));
    }

    /**
     *  Given a list of ids to delete and the array they are in mark items for deletion
     *  Items without an id (not saved in db yet) are just removed from the collection
     *  Items already in the db are marked for deletion and are deleted after "Save Changes"
     */
    removeItemsFromCollection(itemIds: any[], itemCollection: any[]) {
        if (!itemIds || !itemCollection) return;

        const newItemsForDelete = itemIds.filter((x) => x.tempId);

        if (newItemsForDelete.length > 0) {
            newItemsForDelete.forEach((item) => {
                const deleteItemIndex = itemCollection.findIndex((x) => item.tempId == x['tempId']);
                itemCollection.splice(deleteItemIndex, 1);
            });
        }

        const dbIdsForDelete = itemIds.filter((x) => x.id).map((x) => x.id);

        if (dbIdsForDelete.length > 0) {
            dbIdsForDelete.forEach((id) => {
                const itemToDelete = itemCollection.find((x) => x.id == id);
                itemToDelete['active'] = false;
            });
        }

        return itemCollection;
    }

    /**
     *  Apply a temporary id to a new item that is added which will be saved on "Save changes"
     *  This is useful for subsequent edits after an item is added but a user wants to edit a field
     */
    addItem(item) {
        if (!item) return;
        item['tempId'] = uuidv4();
        return item;
    }

    undoMarkForDeletion(itemCollection: any[]) {
        if (!itemCollection) return;

        itemCollection.forEach((x) => {
            if (!x.active) x.active = true;
        });

        return itemCollection;
    }

    /**
     * Given an edited item and an item collection update properties of an item
     * If an item already has an id (is saved to the db) update the values and persist changes after "Save changes" is clicked
     * If an item is new, just update its values
     * @param editedItem - item passed in to the top level component, eg. edited HazmatItem passed to VesselVisitDocumentEdit
     * @param itemCollection - collection that an item is a part of, eg. this.documentDisplayModel.hazmatItems
     */
    editItemInCollection(editedItem: any, itemCollection: any[]) {
        if (!editedItem || !itemCollection) return;

        let itemToEdit;

        if (editedItem.id) {
            itemToEdit = itemCollection.find((x) => x.id == editedItem.id);
            itemToEdit['markedForEdit'] = true;
        } else {
            itemToEdit = itemCollection.find((x) => x['tempId'] == editedItem.tempId);
        }

        itemToEdit = Object.assign(itemToEdit, editedItem);

        return itemCollection;
    }

    setFormRequiredFields(formControlNames: string[], formGroup: UntypedFormGroup) {
        formControlNames.forEach((formControl) => {
            const fgRef = formGroup.get(formControl);

            fgRef.setValidators(requiredValidator());
            fgRef.updateValueAndValidity();
        });
    }

    toggleFormControlsDisabledState(
        formControlNames: string[],
        formGroup: UntypedFormGroup,
        action: 'enable' | 'disable'
    ) {
        if (action === 'enable') {
            formControlNames.forEach((formControl) => {
                formGroup.get(formControl).enable();
            });
        }
        if (action === 'disable') {
            formControlNames.forEach((formControl) => {
                formGroup.get(formControl).disable();
            });
        }
    }

    /**
     * Navigate to a given route and ignore dirty-check-guard.
     * Used in save methods which navigate out of current component after successful action
     * @param route example
     * ['/administration/vessel-registry/list'] or
     * ['/administration/vessel-registry/view', 123]
     */
    navigateAfterSave(route: (string | number)[]) {
        this.router.navigate(route, { state: { bypassDirtyCheckGuard: true } });
    }

    reset<T>(data: T, classInstance: { new (data): T }) {
        this.model = new classInstance(data);
        this.form.reset(this.model);
        this.isBusy = false;
    }

    public onTabSelect(event: SelectEvent) {
        this.initialSelectedTabIndex = event.index;
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {
                tab: this.initialSelectedTabIndex
            },
            replaceUrl: true
        });
    }
}
